"use client";

import { PriceAnchor } from "@/components/services/price-anchor";
import { useState, type ReactNode } from "react";
import Link from "next/link";
import { m } from "framer-motion";
import {
  Share2,
  Calendar,
  Send,
  ArrowRight,
  Check,
  X,
  Sparkles,
  TrendingUp,
  Eye,
  Heart,
  Video,
  BarChart3,
} from "lucide-react";
import { ReelPhoneMockup, SocialReportMockup } from "@/components/mockups/service-mockups";
import { MockupImage } from "@/components/services/mockup-image";
/** Server-gerenderte Abschnitte des Seitenschemas (Auftrag 2026-10-28, Paket 7), vom page.tsx übergeben. */
export type ServiceSlots = {
  afterHero?: ReactNode;
  afterPrice?: ReactNode;
  beforeProcess?: ReactNode;
  /** Ablauf als gemeinsame Zeitleiste (ProcessSteps) – ersetzt den früheren eigenen Block. */
  process?: ReactNode;
  beforeCta?: ReactNode;
  /** Hintergrund des CTA-Abschnitts, damit der Wechsel weiß/grau auch mit und ohne FAQ aufgeht (Paket 12, Nr. 8a). */
  ctaTone?: "primary" | "secondary";
};


/* ─────────────────────────────────────────────────────────────
 * Reichweite-Rechner
 *
 * Vereinfachte Annahmen (Branchen-Mittel TikTok/Instagram Reels DE):
 *   • Ø Views pro Reel je nach Account-Status
 *   • Engagement-Rate ~3–7 % (Like/Comment/Save/Share je Reach)
 *   • Profil-Klicks ~2 % der Reach
 *   • Daraus geschätzt: Neue Follower, Profil-Besucher
 * ──────────────────────────────────────────────────────────── */
function ReachCalculator() {
  const [postsPerWeek, setPostsPerWeek] = useState(3);
  const [avgViews, setAvgViews] = useState(8000);

  const postsPerMonth = postsPerWeek * 4;
  const monthlyReach = postsPerMonth * avgViews;
  const profileVisits = Math.round(monthlyReach * 0.02);
  const newFollowers = Math.round(profileVisits * 0.12);
  const engagement = Math.round(monthlyReach * 0.05);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 lg:p-8"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow-lg)",
      }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-pink-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-brand-400/10 blur-3xl" />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          <Eye className="h-3.5 w-3.5" />
          Reach-Rechner
        </span>
        <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
          Was kann Social Media in Reichweite – ehrlich gerechnet?
        </h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          Wie oft veröffentlichen Sie, wie viele Views erreichen Ihre Posts
          im Schnitt? Wir kalkulieren Monats-Reach, Profil-Besucher und neue
          Follower – realistisch für TikTok- und Reels-Format.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6">
            <SliderField id="ppw" label="Posts pro Woche" value={postsPerWeek} min={1} max={14} step={1} format={(v) => `${v} ${v === 1 ? "Post" : "Posts"}`} onChange={setPostsPerWeek} accent="brand" />
            <SliderField id="views" label="Ø Views pro Post" value={avgViews} min={500} max={100000} step={500} format={(v) => v.toLocaleString("de-DE")} onChange={setAvgViews} accent="brand" />
            <div className="rounded-xl border p-4 text-xs leading-relaxed" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}>
              <p>
                Faustregel: Neue Accounts schaffen 500–2 000 Views pro Reel,
                gewachsene Profile mit gutem Content erreichen 5 000–50 000+.
                Conversion (Reach → Profil) ~2 %, Conversion (Profil → Follow) ~12 %.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-xl p-5 lg:p-6" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <ReachStat label="Monatliche Reach" value={monthlyReach} Icon={Eye} accent />
            <ReachStat label="Engagement (Likes/Saves/Shares)" value={engagement} Icon={Heart} />
            <ReachStat label="Profil-Besucher" value={profileVisits} Icon={Share2} />
            <div className="flex items-baseline justify-between gap-3 border-t pt-3" style={{ borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Neue Follower / Monat</p>
              <p className="text-2xl font-bold tabular-nums sm:text-3xl" style={{ color: "var(--fi-brand-fg)" }}>+{newFollowers.toLocaleString("de-DE")}</p>
            </div>

            <div className="mt-3 rounded-lg border-l-4 p-4" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--fi-brand-fg)" }}>Realistische Skalierung</p>
              <p className="mt-1 text-sm leading-snug" style={{ color: "var(--fi-brand-fg)" }}>
                Mit 3 Posts/Woche und einer konsistenten Content-Linie sind in
                <strong> 6–12 Monaten 5 000 – 25 000 Reach pro Reel</strong> realistisch –
                ohne Werbe-Budget.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReachStat({ label, value, Icon, accent }: { label: string; value: number; Icon: typeof Eye; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
        <Icon className="h-3 w-3" />
        {label}
      </p>
      <p className="text-xl font-semibold tabular-nums" style={{ color: accent ? "var(--text)" : "var(--text-secondary)" }}>
        {value.toLocaleString("de-DE")}
      </p>
    </div>
  );
}

function SliderField({ id, label, value, min, max, step, format, onChange }: { id: string; label: string; value: number; min: number; max: number; step: number; format: (v: number) => string; onChange: (v: number) => void; accent?: "brand" | "error" }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold" style={{ color: "var(--text)" }}>{label}</label>
        <span className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>{format(value)}</span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="mt-2 w-full accent-brand-500" />
      <div className="mt-1 flex justify-between text-[10px]" style={{ color: "var(--text-quaternary)" }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Vergleich
 * ──────────────────────────────────────────────────────────── */
function ComparisonBlock() {
  const without = [
    'Posten, weil „man muss" – ohne Strategie und Plan',
    "Stockbilder + generische Caption – sieht aus wie alle anderen",
    "Eine Plattform für alle: gleiches Format auf Insta, LinkedIn, TikTok",
    "Keine Auswertung – Sie wissen nicht, was funktioniert hat",
    "Sporadische Posts, wenn gerade Zeit ist",
  ];
  const withUs = [
    "Strategie + Redaktions-Plan pro Monat, klare Themen-Säulen",
    "Echter Content: vor Ort gedreht, mit Storytelling",
    "Plattform-gerechtes Format (vertikal für Reels, anders für LinkedIn)",
    "Monatlicher Report mit Reach, Followern, Engagement-Rate",
    "Konsequente Frequenz – 3 Reels/Woche, das ganze Jahr",
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-2xl border p-5 lg:p-6" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--fi-error-bg)", color: "var(--fi-error-fg)" }}>Pflicht-Posten</span>
        <h3 className="mt-3 text-xl font-semibold" style={{ color: "var(--text)" }}>„Wir müssen ja was machen."</h3>
        <ul className="mt-4 space-y-2">
          {without.map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fi-error-bg)" }}>
                <X className="h-3 w-3" style={{ color: "var(--fi-error-fg)" }} />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative overflow-hidden rounded-2xl border p-5 lg:p-6" style={{ borderColor: "var(--color-brand-500)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}>
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-400/15 blur-2xl" />
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          <Sparkles className="h-3 w-3" /> DMD-Social
        </span>
        <h3 className="mt-3 text-xl font-semibold" style={{ color: "var(--text)" }}>„Wir werden tatsächlich erkannt."</h3>
        <ul className="mt-4 space-y-2">
          {withUs.map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text)" }}>
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fi-success-bg)" }}>
                <Check className="h-3 w-3" style={{ color: "var(--fi-success-fg)" }} />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function SocialMediaClient({ afterHero, afterPrice, beforeProcess, process, beforeCta, ctaTone = "secondary" }: ServiceSlots) {
  return (
    <>
      <nav className="mx-auto max-w-7xl px-4 py-4 lg:px-8" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm" style={{ color: "var(--text-tertiary)" }}>
          <li><Link href="/" className="transition-colors hover:text-brand-500">Startseite</Link></li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li><Link href="/dienstleistungen" className="transition-colors hover:text-brand-500">Dienstleistungen</Link></li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li className="font-medium" style={{ color: "var(--text)" }}>Social Media Marketing</li>
        </ol>
      </nav>

      <section className="pb-10 pt-4 lg:pb-12 lg:pt-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
                <Share2 className="h-3.5 w-3.5 text-brand-500" /> Reels · Stories · Short-Form-Content
              </div>
              <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
                Reels, die im <span className="text-brand-500">Algorithmus oben</span> landen.
              </h1>
              <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                Wir planen Ihre Content-Strategie, drehen Reels vor Ort,
                schneiden sie plattform-gerecht und veröffentlichen sie nach
                Redaktions-Plan. <strong style={{ color: "var(--text)" }}>Keine Stockbilder,
                kein KI-Müll – echter Content, der hängenbleibt.</strong>
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  { Icon: TrendingUp, text: "TikTok-Algorithmus-konform" },
                  { Icon: Video, text: "Reels & Shorts" },
                  { Icon: BarChart3, text: "Monatlicher Report" },
                ].map((p) => {
                  const Icon = p.Icon;
                  return (
                    <span key={p.text} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
                      <Icon className="h-3.5 w-3.5 text-brand-500" />
                      {p.text}
                    </span>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link href="/beratung" className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md">
                  <Calendar className="h-5 w-5" /> Kostenloses Erstgespräch
                </Link>
                <Link href="/anfrage" className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all duration-150 hover:shadow-sm" style={{ color: "var(--text-secondary)", borderColor: "var(--border-secondary)" }}>
                  <Send className="h-4 w-4" /> Frage stellen
                </Link>
              </div>
            </div>
            <div>
              <ReelPhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {afterHero}

      {/* Preisanker – Texte aus src/lib/facts.ts (PRICE_ANCHORS), bewusst ohne Beträge (Entscheidung 2026-10-21) */}
      <PriceAnchor service="social-media-marketing" />
      {afterPrice}

      {/* CALCULATOR (weiß, interaktiv) */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <ReachCalculator />
        </div>
      </section>

      {/* MOCKUPS (grau, Bilder) */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div className="mx-auto max-w-2xl text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.4 }}>
            <p className="text-sm font-semibold text-brand-500">Was wir liefern</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>Drei Bausteine, ein System.</h2>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>Content-Produktion, Redaktions-Plan, monatliche Auswertung – im Paket.</p>
          </m.div>
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <ReelPhoneMockup />
            <MockupImage name="social-redaktionsplan.webp" alt="Redaktionsplan einer Woche für die Beispielbetrieb GmbH: Beiträge je Wochentag mit Format, Plattform und Status" />
            <SocialReportMockup />
          </div>
        </div>
      </section>

      {/* VERGLEICH (weiß, zwei Karten) */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div className="mx-auto max-w-2xl text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}>
            <p className="text-sm font-semibold text-brand-500">Vorher / Nachher</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>„Wir müssen was machen" vs. echte Strategie.</h2>
          </m.div>
          <div className="mt-8"><ComparisonBlock /></div>
        </div>
      </section>

      {beforeProcess}

      {process}

      {beforeCta}

      {/* CTA */}
      <section className="py-12 lg:py-16" style={ctaTone === "secondary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}>
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
          <m.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>Bereit, gesehen zu werden?</h2>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>Im Erstgespräch klären wir, ob Social Media in Ihrer Branche überhaupt das richtige Werkzeug ist – und mit welchem Format Sie starten sollten.</p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link href="/beratung" className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all hover:bg-brand-600 hover:shadow-md">
                <Calendar className="h-5 w-5" /> Kostenloses Erstgespräch
              </Link>
              <Link href="/anfrage" className="inline-flex items-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all hover:shadow-sm" style={{ color: "var(--text-secondary)", borderColor: "var(--border-secondary)" }}>
                Anfrage senden
              </Link>
            </div>
          </m.div>
        </div>
      </section>

      <section className="py-12 lg:py-16" style={ctaTone === "primary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-center text-xl font-semibold" style={{ color: "var(--text)" }}>Das könnte Sie auch interessieren</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {[
              { name: "Paid Ads – Meta & TikTok Ads", href: "/dienstleistungen/paid-ads" },
              { name: "Branding – Visuelle Identität", href: "/dienstleistungen/branding" },
              { name: "Webdesign – starke Landingpage", href: "/dienstleistungen/webdesign" },
            ].map((s) => (
              <Link key={s.href} href={s.href} className="group inline-flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all hover:shadow-sm" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                {s.name}
                <ArrowRight className="h-3.5 w-3.5 text-brand-500 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
